export const ROUTES = {
  LOGIN: '/login',
  FORGOT_PASSWORD: '/forgot-password',
  VERIFY_OTP: '/verify-otp',
  RESET_PASSWORD: '/reset-password',
  DASHBOARD: '/dashboard',
  PROFILE: '/profile',
  PRODUCTS: '/products',
  PRODUCT_CREATE: '/products/create',
  ORDERS: '/orders',
  CATEGORIES: '/categories',
  SUBCATEGORIES: '/subcategories',
  BRANDS: '/brands',
  STATES: '/states',
  DISTRICTS: '/districts',
  ASSEMBLIES: '/assemblies',
  CONSTITUENCIES: '/constituencies',
  ATTRIBUTES: '/attributes',
  ATTRIBUTE_VALUES: '/attribute-values',
  BANNERS: '/banners',
  COLLECTIONS: '/collections',
  MEDIA: '/media',
  INVENTORY: '/inventory',
  REVIEWS: '/reviews',
  COUPONS: '/coupons',
  COUPON_CREATE: '/coupons/create',
  REPORTS: '/reports',
  CUSTOMERS: '/customers',
  CUSTOMER_CREATE: '/customers/create',
  ENQUIRIES: '/enquiries',
  CONTACTS: '/contacts',
  NOTIFICATIONS: '/notifications',
  SETTINGS: '/settings',
  USERS: '/users',
  USER_CREATE: '/users/create',
  CMS: '/cms',
  SEO: '/seo',
  EMAIL_TEMPLATES: '/email-templates',
  BLOGS: '/blogs',
  BLOG_CREATE: '/blogs/create',
} as const;

export type AppRoute = (typeof ROUTES)[keyof typeof ROUTES];

export interface RouteMeta {
  documentTitle: string;
  heading?: string;
  subtitle?: string;
}

export interface BreadcrumbItem {
  label: string;
  path?: string;
}

const CONTENT_CHILD_ROUTES: AppRoute[] = [
  ROUTES.BANNERS,
  ROUTES.COLLECTIONS,
  ROUTES.MEDIA,
  ROUTES.CMS,
  ROUTES.SEO,
  ROUTES.EMAIL_TEMPLATES,
  ROUTES.BLOGS,
];

const SALES_CHILD_ROUTES: AppRoute[] = [ROUTES.COUPONS, ROUTES.REPORTS];

const CATALOG_CHILD_ROUTES: AppRoute[] = [
  ROUTES.CATEGORIES,
  ROUTES.SUBCATEGORIES,
  ROUTES.BRANDS,
  ROUTES.STATES,
  ROUTES.DISTRICTS,
  ROUTES.ASSEMBLIES,
  ROUTES.CONSTITUENCIES,
  ROUTES.ATTRIBUTES,
  ROUTES.ATTRIBUTE_VALUES,
  ROUTES.PRODUCTS,
  ROUTES.INVENTORY,
  ROUTES.REVIEWS,
];

export const ROUTE_META: Record<AppRoute, RouteMeta> = {
  [ROUTES.LOGIN]: {
    documentTitle: 'Login',
    heading: 'Welcome back',
    subtitle: 'Sign in to your admin account',
  },
  [ROUTES.FORGOT_PASSWORD]: {
    documentTitle: 'Forgot Password',
    heading: 'Forgot password',
    subtitle: "Enter your email and we'll send you an OTP",
  },
  [ROUTES.VERIFY_OTP]: {
    documentTitle: 'Verify OTP',
    heading: 'Verify OTP',
    subtitle: 'Enter the 6-digit code sent to your email',
  },
  [ROUTES.RESET_PASSWORD]: {
    documentTitle: 'Reset Password',
    heading: 'Reset password',
    subtitle: 'Create a new password for your account',
  },
  [ROUTES.DASHBOARD]: { documentTitle: 'Dashboard' },
  [ROUTES.PROFILE]: {
    documentTitle: 'My Profile',
    heading: 'My profile',
    subtitle: 'Update your account details and password',
  },
  [ROUTES.PRODUCTS]: {
    documentTitle: 'Products',
    heading: 'Product Manager',
    subtitle: 'Create and manage simple and variable products with variants',
  },
  [ROUTES.PRODUCT_CREATE]: {
    documentTitle: 'Add Product',
    heading: 'Add Product',
    subtitle: 'Create a new product',
  },
  [ROUTES.ORDERS]: {
    documentTitle: 'Orders',
    heading: 'Order Manager',
    subtitle: 'View and track customer orders, payments, and fulfillment status',
  },
  [ROUTES.CATEGORIES]: {
    documentTitle: 'Categories',
    heading: 'Category Manager',
    subtitle: 'Create, edit, delete, and reorder product categories',
  },
  [ROUTES.SUBCATEGORIES]: {
    documentTitle: 'Subcategories',
    heading: 'Subcategory Manager',
    subtitle: 'Manage subcategories under each product category',
  },
  [ROUTES.BRANDS]: {
    documentTitle: 'Brands',
    heading: 'Brand Manager',
    subtitle: 'Create, edit, delete, and reorder product brands',
  },
  [ROUTES.STATES]: {
    documentTitle: 'States',
    heading: 'State Manager',
    subtitle: 'Create and manage states for catalogue filtering',
  },
  [ROUTES.DISTRICTS]: {
    documentTitle: 'Districts',
    heading: 'District Manager',
    subtitle: 'Create districts under selected states',
  },
  [ROUTES.ASSEMBLIES]: {
    documentTitle: 'Assemblies',
    heading: 'Assembly Manager',
    subtitle: 'Create assemblies under selected state and district',
  },
  [ROUTES.CONSTITUENCIES]: {
    documentTitle: 'Constituencies',
    heading: 'Constituency Manager',
    subtitle: 'Create constituencies under selected states',
  },
  [ROUTES.ATTRIBUTES]: {
    documentTitle: 'Attributes',
    heading: 'Attribute Manager',
    subtitle: 'Create and manage publication attributes such as format and access type',
  },
  [ROUTES.ATTRIBUTE_VALUES]: {
    documentTitle: 'Sub Attributes',
    heading: 'Sub Attribute Manager',
    subtitle: 'Manage subtitle, title, description, and image for each attribute value',
  },
  [ROUTES.BANNERS]: {
    documentTitle: 'Banners',
    heading: 'Banner Manager',
    subtitle: 'Manage homepage banners and sliders',
  },
  [ROUTES.COLLECTIONS]: {
    documentTitle: 'Collections',
    heading: 'Collection Manager',
    subtitle: 'Manage featured collections such as New Arrival and Best Seller',
  },
  [ROUTES.MEDIA]: {
    documentTitle: 'Media Manager',
    heading: 'Media Manager',
    subtitle: 'Upload, organize, and manage gallery images',
  },
  [ROUTES.INVENTORY]: {
    documentTitle: 'Inventory',
    heading: 'Inventory Manager',
    subtitle: 'Track SKU stock levels by warehouse and adjust quantities',
  },
  [ROUTES.REVIEWS]: {
    documentTitle: 'Reviews',
    heading: 'Review Manager',
    subtitle: 'Approve or reject customer product reviews with ratings and comments',
  },
  [ROUTES.COUPONS]: {
    documentTitle: 'Coupons',
    heading: 'Coupon Manager',
    subtitle: 'Create and manage discount coupons for checkout',
  },
  [ROUTES.COUPON_CREATE]: {
    documentTitle: 'Add Coupon',
    heading: 'Add Coupon',
    subtitle: 'Create a new discount coupon',
  },
  [ROUTES.REPORTS]: {
    documentTitle: 'Reports',
    heading: 'Reports Manager',
    subtitle: 'Analyze sales, inventory, and customer performance with charts and exports',
  },
  [ROUTES.CUSTOMERS]: {
    documentTitle: 'Customers',
    heading: 'Customer Manager',
    subtitle: 'View and manage customer profiles, contact details, and account status',
  },
  [ROUTES.ENQUIRIES]: {
    documentTitle: 'Enquiries',
    heading: 'Enquiry Manager',
    subtitle: 'Review institutional inquiry submissions from the website',
  },
  [ROUTES.CONTACTS]: {
    documentTitle: 'Contact Us',
    heading: 'Contact Us Manager',
    subtitle: 'Review contact form messages from the website',
  },
  [ROUTES.NOTIFICATIONS]: {
    documentTitle: 'Notification Manager',
    heading: 'Notification Manager',
    subtitle: 'Send email and push notifications to active customers',
  },
  [ROUTES.CUSTOMER_CREATE]: {
    documentTitle: 'Add Customer',
    heading: 'Add Customer',
    subtitle: 'Create a new customer profile',
  },
  [ROUTES.SETTINGS]: {
    documentTitle: 'Settings',
    heading: 'Settings',
    subtitle: 'Configure website, payments, and email delivery',
  },
  [ROUTES.USERS]: {
    documentTitle: 'User & Role Manager',
    heading: 'User & Role Manager',
    subtitle: 'Admin users and permissions',
  },
  [ROUTES.USER_CREATE]: {
    documentTitle: 'Add Admin User',
    heading: 'Add Admin User',
    subtitle: 'Create a new staff account',
  },
  [ROUTES.CMS]: {
    documentTitle: 'CMS Manager',
    heading: 'CMS Manager',
    subtitle: 'Manage About Us, policies, and terms pages',
  },
  [ROUTES.SEO]: {
    documentTitle: 'SEO Manager',
    heading: 'SEO Manager',
    subtitle: 'Meta tags, alt text, robots, canonical, sitemap, and 301 redirects',
  },
  [ROUTES.EMAIL_TEMPLATES]: {
    documentTitle: 'Email Template Manager',
    heading: 'Email Template Manager',
    subtitle: 'Create templates and update the default email signature',
  },
  [ROUTES.BLOGS]: {
    documentTitle: 'Blog Manager',
    heading: 'Blog Manager',
    subtitle: 'Create and publish blog articles',
  },
  [ROUTES.BLOG_CREATE]: {
    documentTitle: 'Add Blog Article',
    heading: 'Add Blog Article',
    subtitle: 'Create a new blog article',
  },
};

export function getRouteMeta(pathname: string): RouteMeta | undefined {
  if (/^\/orders\/[^/]+$/.test(pathname)) {
    return {
      documentTitle: 'Order Details',
      heading: 'Order Details',
      subtitle: 'View order items, payment, shipping, and fulfillment information',
    };
  }

  if (/^\/users\/[^/]+\/edit$/.test(pathname)) {
    return {
      documentTitle: 'Edit Admin User',
      heading: 'Edit Admin User',
      subtitle: 'Update staff account details',
    };
  }

  if (/^\/customers\/[^/]+\/edit$/.test(pathname)) {
    return {
      documentTitle: 'Edit Customer',
      heading: 'Edit Customer',
      subtitle: 'Update customer details',
    };
  }

  if (/^\/customers\/[^/]+$/.test(pathname) && pathname !== ROUTES.CUSTOMER_CREATE) {
    return {
      documentTitle: 'Customer Details',
      heading: 'Customer Details',
      subtitle: 'View customer profile information',
    };
  }

  if (/^\/coupons\/[^/]+\/edit$/.test(pathname)) {
    return {
      documentTitle: 'Edit Coupon',
      heading: 'Edit Coupon',
      subtitle: 'Update coupon details',
    };
  }

  if (/^\/products\/[^/]+\/edit$/.test(pathname)) {
    return {
      documentTitle: 'Edit Product',
      heading: 'Edit Product',
      subtitle: 'Update product details',
    };
  }

  if (/^\/blogs\/[^/]+\/edit$/.test(pathname)) {
    return {
      documentTitle: 'Edit Article',
      heading: 'Edit Article',
      subtitle: 'Update blog article',
    };
  }

  if (/^\/blogs\/[^/]+\/preview$/.test(pathname)) {
    return {
      documentTitle: 'Preview Article',
      heading: 'Preview Article',
      subtitle: 'See how your article will look',
    };
  }

  return ROUTE_META[pathname as AppRoute];
}

export function getBreadcrumbs(pathname: string): BreadcrumbItem[] {
  if (pathname === ROUTES.DASHBOARD) {
    return [{ label: 'Dashboard' }];
  }

  if (pathname === ROUTES.ORDERS) {
    return [{ label: 'Orders' }];
  }

  if (/^\/orders\/[^/]+$/.test(pathname)) {
    return [{ label: 'Orders', path: ROUTES.ORDERS }, { label: 'Order Details' }];
  }

  if (pathname === ROUTES.USERS) {
    return [{ label: 'User & Role Manager' }];
  }

  if (pathname === ROUTES.USER_CREATE || /^\/users\/[^/]+\/edit$/.test(pathname)) {
    const label = pathname === ROUTES.USER_CREATE ? 'Add Admin User' : 'Edit Admin User';
    return [{ label: 'User & Role Manager', path: ROUTES.USERS }, { label }];
  }

  if (pathname === ROUTES.CUSTOMERS) {
    return [{ label: 'Customers' }];
  }

  if (pathname === ROUTES.ENQUIRIES) {
    return [{ label: 'Enquiries' }];
  }

  if (pathname === ROUTES.CONTACTS) {
    return [{ label: 'Contact Us' }];
  }

  if (pathname === ROUTES.NOTIFICATIONS) {
    return [{ label: 'Notification Manager' }];
  }

  if (pathname === ROUTES.CUSTOMER_CREATE || /^\/customers\/[^/]+\/edit$/.test(pathname)) {
    const label = pathname === ROUTES.CUSTOMER_CREATE ? 'Add Customer' : 'Edit Customer';
    return [{ label: 'Customers', path: ROUTES.CUSTOMERS }, { label }];
  }

  if (/^\/customers\/[^/]+$/.test(pathname)) {
    return [{ label: 'Customers', path: ROUTES.CUSTOMERS }, { label: 'Customer Details' }];
  }

  if (pathname === ROUTES.PROFILE) {
    return [{ label: 'Dashboard', path: ROUTES.DASHBOARD }, { label: 'My Profile' }];
  }

  if (CONTENT_CHILD_ROUTES.includes(pathname as AppRoute)) {
    const meta = getRouteMeta(pathname);
    const onBanners = pathname === ROUTES.BANNERS;
    const onMedia = pathname === ROUTES.MEDIA;

    return [
      { label: 'Content', ...(onBanners || onMedia ? {} : { path: ROUTES.BANNERS }) },
      { label: meta?.documentTitle ?? 'Content' },
    ];
  }

  if (SALES_CHILD_ROUTES.includes(pathname as AppRoute)) {
    const meta = getRouteMeta(pathname);

    return [{ label: 'Sales', path: ROUTES.COUPONS }, { label: meta?.documentTitle ?? 'Sales' }];
  }

  if (pathname === ROUTES.COUPON_CREATE || /^\/coupons\/[^/]+\/edit$/.test(pathname)) {
    const label = pathname === ROUTES.COUPON_CREATE ? 'Add Coupon' : 'Edit Coupon';
    return [
      { label: 'Sales', path: ROUTES.COUPONS },
      { label: 'Coupons', path: ROUTES.COUPONS },
      { label },
    ];
  }

  if (pathname === ROUTES.PRODUCT_CREATE || /^\/products\/[^/]+\/edit$/.test(pathname)) {
    const label = pathname === ROUTES.PRODUCT_CREATE ? 'Add Product' : 'Edit Product';
    return [
      { label: 'Catalog', path: ROUTES.CATEGORIES },
      { label: 'Products', path: ROUTES.PRODUCTS },
      { label },
    ];
  }

  if (pathname === ROUTES.BLOG_CREATE || /^\/blogs\/[^/]+\/edit$/.test(pathname)) {
    const label = pathname === ROUTES.BLOG_CREATE ? 'Add Article' : 'Edit Article';
    return [
      { label: 'Content', path: ROUTES.BANNERS },
      { label: 'Blog Manager', path: ROUTES.BLOGS },
      { label },
    ];
  }

  if (/^\/blogs\/[^/]+\/preview$/.test(pathname)) {
    return [
      { label: 'Content', path: ROUTES.BANNERS },
      { label: 'Blog Manager', path: ROUTES.BLOGS },
      { label: 'Preview Article' },
    ];
  }

  if (pathname === ROUTES.CMS) {
    return [{ label: 'Content', path: ROUTES.BANNERS }, { label: 'CMS Manager' }];
  }

  if (pathname === ROUTES.EMAIL_TEMPLATES) {
    return [{ label: 'Content', path: ROUTES.BANNERS }, { label: 'Email Template Manager' }];
  }

  if (/^\/products\/[^/]+\/view$/.test(pathname)) {
    return [
      { label: 'Catalog', path: ROUTES.CATEGORIES },
      { label: 'Products', path: ROUTES.PRODUCTS },
      { label: 'View Product' },
    ];
  }

  if (CATALOG_CHILD_ROUTES.includes(pathname as AppRoute)) {
    const meta = getRouteMeta(pathname);
    const onCategories = pathname === ROUTES.CATEGORIES;
    const onInventory = pathname === ROUTES.INVENTORY;

    return [
      { label: 'Catalog', ...(onCategories || onInventory ? {} : { path: ROUTES.CATEGORIES }) },
      { label: meta?.documentTitle ?? 'Catalog' },
    ];
  }

  const meta = getRouteMeta(pathname);
  if (meta) {
    return [{ label: meta.documentTitle }];
  }

  return [{ label: 'Dashboard', path: ROUTES.DASHBOARD }];
}
